'use client';

import { useEffect, useMemo, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useStore } from 'zustand';

import { PatientSelectionModal } from './patient-selection-modal.component';
import { buildPatientOptions } from './_utils/build-patient-options.util';
import { requiresPhoneConfirmation } from './_utils/registration-freshness.util';

import { authStore } from '@/store/auth.store';
import {
  closePatientSelection,
  patientSelectionStore,
  selectPatient,
  setPendingPatient,
} from '@/store/patient-selection/patient-selection.store';

export function PatientSelectionHost() {
  const isOpen = useStore(patientSelectionStore, (state) => state.isOpen);
  const request = useStore(patientSelectionStore, (state) => state.request);
  const pendingPatient = useStore(patientSelectionStore, (state) => state.pendingPatient);
  const authenticatedUser = useStore(authStore, (state) => state.authenticatedUser);
  const phoneConfirmedAt = useStore(authStore, (state) => state.phoneConfirmedAt);

  const pathname = usePathname();
  const prevPathnameRef = useRef(pathname);

  useEffect(() => {
    if (prevPathnameRef.current === pathname) {
      return;
    }

    prevPathnameRef.current = pathname;
    closePatientSelection();
  }, [pathname]);

  const ageRange = useMemo(
    () => ({ minAge: request?.ageRange.minAge, maxAge: request?.ageRange.maxAge }),
    [request?.ageRange.minAge, request?.ageRange.maxAge],
  );

  const patients = useMemo(
    () => buildPatientOptions(authenticatedUser, ageRange),
    [authenticatedUser, ageRange],
  );

  const [holderPhone = ''] = authenticatedUser?.contact?.phones ?? [];

  const shouldConfirmPhone = requiresPhoneConfirmation({
    updatedAt: authenticatedUser?.updatedAt,
    phoneConfirmedAt,
  });

  return (
    <PatientSelectionModal
      open={isOpen}
      patients={patients}
      ageRange={ageRange}
      requiresPhoneConfirmation={shouldConfirmPhone}
      pendingPatient={pendingPatient}
      holderPhone={holderPhone}
      onClose={closePatientSelection}
      onSelectPatient={selectPatient}
      onRequestPhoneConfirmation={setPendingPatient}
    />
  );
}
